import React, { useState } from 'react';
import { TableData } from '../../../components/organisms/Table/TableData';
import { useMutation, useQuery } from '@apollo/client';
import QUERY_ELEVES_WITH_ORDERS_IN_CLASSE from '../../../graphql/query/classe/queryElevesWithOrders';
import { LoadingContainer } from 'components/molecules/Loaders/LoadingContainer';
import { ErrorContainer } from 'components/organisms/Error/ErrorContainer';
import { GraphQLError } from 'graphql';
import { MobileTabLineControl } from 'components/molecules/Table/MobileTabLine/MobileTabLineControl';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { TableDataLineControl } from '../../../components/organisms/Table/TableData/TableDataLineControl';
import {
  EleveCommandes,
  GetOrdersByEleveInClasseQuery
} from '../../../graphql/generated/graphql';
import { UPDATE_CONTROL_STATUS } from '../../../graphql/mutation/commande/updateControlStatus';
import { TableDivider } from '../../../components/atoms/Dividers/TableDivider';
import { EmptyDataMessage } from '../../../components/molecules/EmptyDataMessage';
import toast from 'react-hot-toast';
import { FeatureContainer } from 'components/molecules/Containers/FeatureContainer';
import { RadioOrderNumber } from 'components/atoms/Inputs/RadioOrderNumber';
import { getNumberOfOrdersByPaymentStatus } from 'helpers/commande/getNumberOfOrdersByPaymentStatus';
import { filterOrdersByPaymentStatus } from 'helpers/filters/filterOrdersByPaymentStatus';

export type UserWithOrdersType = {
  firstname: string;
  lastname: string;
  commandes: EleveCommandes[];
};

export const ControlTab = () => {
  const { loading, error, data } = useQuery<GetOrdersByEleveInClasseQuery>(
    QUERY_ELEVES_WITH_ORDERS_IN_CLASSE
  );
  const isDesktop = useMediaQuery('desktop');
  const [updateToControlled] = useMutation(UPDATE_CONTROL_STATUS, {
    onCompleted: () => {
      toast.success('Commande mise à jour', {
        icon: '🎉'
      });
    },
    onError: () => {
      toast.error('Erreur dans la mise à jour de la commande', {
        icon: '😕'
      });
    },
    refetchQueries: [
      { query: QUERY_ELEVES_WITH_ORDERS_IN_CLASSE },
      'getOrdersByEleveInClasse'
    ]
  });
  const [orderFilterValue, setOrderFilterValue] = useState('all');
  if (loading) return <LoadingContainer />;
  if (error)
    return (
      <ErrorContainer
        detailErr={
          new GraphQLError(
            'Erreur dans la récupération des données (classe ou Quiz)'
          )
        }
      />
    );
  const onChange = async e => {
    await updateToControlled({
      variables: {
        id: parseInt(e.target.value),
        controlled: e.target.checked
      }
    });
  };

  const getUserWithOrdersType = (
    dataUser: GetOrdersByEleveInClasseQuery['findElevesWithOrders']['users']
  ): UserWithOrdersType[] =>
    dataUser.map(user => ({
      firstname: user.firstname,
      lastname: user.lastname,
      commandes: user.commandes
    }));

  const usersWithOrders =
    data &&
    getUserWithOrdersType(
      data?.findElevesWithOrders?.users?.filter(
        user => user.commandes.length !== 0
      )
    );

  const tabLabels = [
    'Élève',
    'N° Commande',
    'Client',
    'Total TTC (port inclus + ONG)',
    'Méthode de paiement',
    'Participation ONG',
    'Statut',
    'Modifier',
    'Validé'
  ];
  const ordersNumbers = getNumberOfOrdersByPaymentStatus(usersWithOrders);
  return (
    <>
      <EmptyDataMessage
        text={'Pas encore de commandes à contrôler 🙌'}
        isVisible={usersWithOrders.length === 0}
      />
      {usersWithOrders.length !== 0 && !isDesktop && (
        <FeatureContainer>
          <RadioOrderNumber
            numberOfOrders={ordersNumbers.numberOfOrders}
            numberOfPayedOrders={ordersNumbers.numberOfPayedOrders}
            numberOfUnPayedOrders={ordersNumbers.numberOfUnPayedOrders}
            numberOfCanceledOrders={ordersNumbers.numberOfCanceledOrders}
            isDesktop={true}
            handleClick={value => setOrderFilterValue(value)}
          />
          <TableData tabLabels={tabLabels} noBackground>
            {data &&
              usersWithOrders
                .sort((a, b) => a.lastname.localeCompare(b.lastname))
                .map((user, i) => {
                  const filterOrders = filterOrdersByPaymentStatus(
                    user.commandes,
                    orderFilterValue
                  );
                  return (
                    <>
                      {filterOrders.length > 0 && (
                        <TableDivider isVisible={i !== 0} />
                      )}
                      {filterOrders
                        .filter(commande => commande.status !== 'A_FINALISER')
                        .map((commande, index) => (
                          <TableDataLineControl
                            key={index}
                            user={user.lastname + ' ' + user.firstname}
                            commande={commande}
                            onChange={onChange}
                          />
                        ))}
                    </>
                  );
                })}
          </TableData>
        </FeatureContainer>
      )}
      {usersWithOrders.length !== 0 && isDesktop && (
        <>
          <RadioOrderNumber
            numberOfOrders={ordersNumbers.numberOfOrders}
            numberOfPayedOrders={ordersNumbers.numberOfPayedOrders}
            numberOfUnPayedOrders={ordersNumbers.numberOfUnPayedOrders}
            numberOfCanceledOrders={ordersNumbers.numberOfCanceledOrders}
            isDesktop={false}
            handleClick={value => setOrderFilterValue(value)}
          />
          {data &&
            usersWithOrders
              .sort((a, b) => a.lastname.localeCompare(b.lastname))
              .map(user => (
                <>
                  {filterOrdersByPaymentStatus(user.commandes, orderFilterValue)
                    .filter(commande => commande.status !== 'A_FINALISER')
                    .map((commande, index) => (
                      <MobileTabLineControl
                        key={index}
                        eleve={{
                          firstname: user.firstname,
                          lastname: user.lastname
                        }}
                        commande={commande}
                        onChange={onChange}
                      />
                    ))}
                </>
              ))}
        </>
      )}
    </>
  );
};
